import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { DeviceTree, DriverPath, HubUpdateMode, NoDriverNeeded, StartMenuMock, WinUsbPicker } from "./WindowsVisuals";
import { HubName, HubReadyVisual, InstallProgress, PickHub, PybricksMenu } from "./PybricksVisuals";

const B = ({ children }) => <b className="text-foreground">{children}</b>;
const Steps = ({ children }) => <ol className="list-decimal space-y-2 pl-5 marker:font-bold marker:text-primary">{children}</ol>;
const Tip = ({ children, testId }) => <p className="rounded-xl bg-muted/70 px-4 py-3 text-sm" data-testid={testId}>{children}</p>;
const Ext = ({ href, children, testId }) => (
  <a href={href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-semibold text-primary underline" data-testid={testId}>{children}<ExternalLink className="h-3.5 w-3.5" /></a>
);

const UpdateModeSteps = () => (
  <Steps>
    <li>Turn the hub off and unplug its USB cable.</li>
    <li>Press and <B>hold the Bluetooth button</B> on the top corner of the hub.</li>
    <li>Keep holding it and plug the USB cable into the hub and your computer.</li>
    <li>Let go when the Bluetooth light flashes <B>red, green and blue</B>.</li>
  </Steps>
);

const NEW_HUB_TIP = <Tip>SPIKE Prime hubs bought in 2026 or later use a different button combination. The Pybricks installer shows the right one for your hub.</Tip>;

export const DRIVER_STEPS = [
  {
    id: "driver-update-mode", section: "driver", title: "Put your hub into update mode", Visual: HubUpdateMode,
    Body: () => <><p>Windows can only see the hub for the driver while it's in update mode.</p><UpdateModeSteps />{NEW_HUB_TIP}</>,
  },
  {
    id: "driver-device-manager", section: "driver", title: "Open Device Manager", Visual: StartMenuMock,
    Body: () => <>
      <Steps><li>Right-click the Windows <B>Start</B> button at the bottom left of the screen.</li><li>Choose <B>Device Manager</B>.</li></Steps>
      <Tip>School laptop? You may need a teacher or IT admin to approve this part. You only do it once per computer.</Tip>
    </>,
  },
  {
    id: "driver-find-hub", section: "driver", title: "Find the hub in the list", Visual: () => <DeviceTree group="other" />,
    Body: () => <>
      <Steps><li>Open <B>Other devices</B>.</li><li>Find <B>LEGO Technic Large Hub in DFU Mode</B>. It has a yellow warning sign.</li></Steps>
      <Tip>Can't see it? Check the hub's light is still flashing red, green and blue. Newer hubs may show a different name: pick the one that appeared when you plugged the hub in.</Tip>
    </>,
  },
  {
    id: "driver-pick", section: "driver", title: "Choose the driver yourself", Visual: DriverPath,
    Body: () => (
      <Steps>
        <li>Right-click the hub and choose <B>Update driver</B>.</li>
        <li>Choose <B>Browse my computer for drivers</B>.</li>
        <li>Choose <B>Let me pick from a list of available drivers on my computer</B>.</li>
      </Steps>
    ),
  },
  {
    id: "driver-winusb", section: "driver", title: "Pick WinUSB", Visual: WinUsbPicker,
    Body: () => (
      <Steps>
        <li>Choose <B>Universal Serial Bus devices</B>, then <B>Next</B>.</li>
        <li>Pick <B>WinUsb Device</B> in both lists, then <B>Next</B>.</li>
        <li>Click <B>Yes</B> on the warning, then <B>Close</B>.</li>
      </Steps>
    ),
  },
  {
    id: "driver-check", section: "driver", title: "Check the driver worked", Visual: () => <DeviceTree group="usb" />,
    Body: () => <>
      <p>The hub now shows under <B>Universal Serial Bus devices</B>, with no warning sign. That's the driver done for good on this computer.</p>
      <Tip>Leave the hub plugged in with its light flashing. Next you'll install the Pybricks firmware.</Tip>
    </>,
  },
];

export const NO_DRIVER_STEP = {
  id: "driver-none", section: "driver", title: "No driver needed on this computer", Visual: NoDriverNeeded,
  Body: ({ showWindows }) => <>
    <p>Only Windows needs a USB driver. Mac, Chromebook and Linux can talk to the hub straight away, so you can skip this part.</p>
    <button type="button" onClick={showWindows} className="text-sm font-semibold text-primary underline" data-testid="ready-show-windows-steps">I'm on Windows: show the driver steps</button>
  </>,
};

export const FIRMWARE_STEPS = [
  {
    id: "fw-open", section: "firmware", title: "Open the Pybricks installer", Visual: PybricksMenu,
    Body: () => <>
      <Steps>
        <li>Open <Ext href="https://code.pybricks.com" testId="ready-pybricks-link">code.pybricks.com</Ext> in Chrome or Edge. It's free.</li>
        <li>Open the menu and choose <B>Install Pybricks Firmware</B>.</li>
      </Steps>
      <Tip>The firmware is free to install, and it's what drives your robot.</Tip>
    </>,
  },
  {
    id: "fw-hub", section: "firmware", title: "Choose your hub", Visual: PickHub,
    Body: () => (
      <Steps>
        <li>Pick <B>SPIKE Prime Hub</B> (or <B>MINDSTORMS Robot Inventor Hub</B>).</li>
        <li>Read and accept the licence, then press <B>Next</B>.</li>
      </Steps>
    ),
  },
  {
    id: "fw-name", section: "firmware", title: "Name your hub", Visual: HubName,
    Body: () => <>
      <p>When it asks for a hub name, type a short label you'll also write on a sticker, like <code className="rounded bg-muted px-1.5 py-0.5 text-foreground">HP-R4-07</code> (room 4, robot 7).</p>
      <p>You'll pick this name each time you connect, so make it easy to spot.</p>
    </>,
  },
  {
    id: "fw-update-mode", section: "firmware", title: "Put your hub into update mode", Visual: HubUpdateMode,
    Body: ({ afterDriver }) => <>
      {afterDriver && <Tip testId="ready-still-flashing">Light still flashing red, green and blue? You're already in update mode, so press Next.</Tip>}
      <p>The installer shows these steps too.</p>
      <UpdateModeSteps />{NEW_HUB_TIP}
    </>,
  },
  {
    id: "fw-install", section: "firmware", title: "Install and wait", Visual: InstallProgress,
    Body: () => <>
      <Steps>
        <li>Press <B>Install</B> (or <B>Next</B>) until it asks to connect.</li>
        <li>In the browser pop-up, choose <B>LEGO Technic Large Hub in DFU Mode</B>, then <B>Connect</B>.</li>
        <li>Wait for the progress bar to finish, about a minute. Don't unplug the hub.</li>
      </Steps>
      <Tip>Hub missing from the pop-up? On Windows it needs the USB driver first. <Link to="/firmware" target="_blank" className="font-semibold text-primary underline" data-testid="ready-troubleshoot-link">See the full guide</Link>.</Tip>
    </>,
  },
  {
    id: "fw-restart", section: "firmware", title: "Switch your hub on", Visual: HubReadyVisual,
    Body: () => <>
      <Steps><li>Unplug the USB cable.</li><li>Press the hub's centre button to switch it on.</li></Steps>
      <p>That's it: your hub has Pybricks firmware. Now tell HubPilot how your robot is built.</p>
    </>,
  },
];
